import { InternalDeliveryTransportMode, KPITransportLeadTimeResult } from 'api/model';
import { MEASURES, nf, trendOf } from './panels';
import {
  MonthBars,
  MonthCell,
  MonthTable,
  Note,
  Panel,
  PanelHead,
  Spark,
  TableWrap,
} from './reportStyles';
import { MonthlyLeadTime, MonthSlot, ReportData } from './useReportData';

/* ---------------- månadsvärden ---------------- */

/**
 * Backend nycklar månadsgrupperna på den första i månaden, midnatt UTC, räknat på svensk tid.
 * Nyckeln läses därför i UTC — i lokal tid hamnar den på sista dagen i månaden innan för den
 * som sitter väster om Greenwich.
 */
const inMonth = (date: Date | string, slot: MonthSlot) => {
  const d = new Date(date);
  return d.getUTCFullYear() === slot.year && d.getUTCMonth() === slot.month;
};

const sum = <T,>(items: T[], pick: (item: T) => number) =>
  items.reduce((total, item) => total + pick(item), 0);

/** Samma avrundning som backend gör på periodens andelar, så att månad och år läses lika. */
const share = (part: number, whole: number) =>
  whole > 0 ? Math.round((part / whole) * 1000) / 10 : null;

/**
 * Ett måtts värde för en månad, räknat ur periodens datapunkter.
 *
 * Varje mått räknas som backend räknar periodens total: antal summeras, andelar räknas som
 * summa delar genom summa helhet — aldrig som ett snitt av månadernas andelar. Därför går
 * månaderna att lägga ihop till året, och en månad är samma mått som året, bara kortare.
 *
 * Ledtiden är undantaget. Den är en median och kommer ur en egen hämtning per månad.
 */
const monthValue = (
  key: string,
  d: ReportData,
  slot: MonthSlot,
  leadTime: KPITransportLeadTimeResult | undefined
): number | null => {
  switch (key) {
    case 'delivered':
      return sum(
        d.deliveredCars.dataPoints.filter((p) => inMonth(p.date, slot)),
        (p) => p.deliveredCars
      );
    case 'leadTime':
      return leadTime && leadTime.cars > 0 ? leadTime.medianHours : null;
    case 'truckFill': {
      const runs = d.truckCoverage.fillRates.filter((p) => inMonth(p.date, slot));
      return share(
        sum(runs, (p) => p.loadedCars),
        sum(runs, (p) => p.capacity)
      );
    }
    case 'satisfaction': {
      // Ur betygsfördelningen och inte ur gruppernas snitt, som redan är avrundade till två
      // decimaler — ett snitt av avrundade snitt kan hamna en hundradel fel.
      const counts = d.satisfaction.dataPoints
        .filter((p) => inMonth(p.date, slot))
        .flatMap((p) => p.ratingDistribution);
      const responses = sum(counts, (c) => c.count);
      return responses > 0
        ? Math.round((sum(counts, (c) => c.rating * c.count) / responses) * 100) / 100
        : null;
    }
    case 'cancellations':
      return sum(
        d.cancellations.dataPoints.filter((p) => inMonth(p.date, slot)),
        (p) => p.cases
      );
    case 'truckShare': {
      const rows = d.transportMix.dataPoints.filter((p) => inMonth(p.date, slot));
      return share(
        sum(
          rows.filter((p) => p.mode !== InternalDeliveryTransportMode.FieldTester),
          (p) => p.cars
        ),
        sum(rows, (p) => p.cars)
      );
    }
    case 'utilization': {
      const rows = d.utilization.dataPoints.filter((p) => inMonth(p.date, slot));
      return share(
        sum(rows, (p) => p.workedHours),
        sum(rows, (p) => p.capHours)
      );
    }
    default:
      return null;
  }
};

export interface MonthlySeries {
  months: MonthSlot[];
  /** Per mått: ett värde per månad, i samma ordning som `months`. */
  current: Record<string, (number | null)[]>;
  /** Samma månader i fjol, eller null när jämförelsen saknas. */
  previous: Record<string, (number | null)[]> | null;
}

/**
 * Alla mått per månad, för perioden och för samma månader i fjol.
 *
 * Fjolårets månader paras ihop med årets på plats i listan, inte på datum. Båda börjar 1 januari
 * och slutar på samma dag, så plats för plats är samma månad.
 */
export const monthlySeries = (
  months: MonthSlot[],
  comparisonMonths: MonthSlot[],
  data: ReportData,
  previous: ReportData | null,
  leadTime: MonthlyLeadTime
): MonthlySeries => {
  const current: Record<string, (number | null)[]> = {};
  const before: Record<string, (number | null)[]> = {};
  MEASURES.forEach((m) => {
    current[m.key] = months.map((slot, i) => monthValue(m.key, data, slot, leadTime.current?.[i]));
    if (previous) {
      before[m.key] = months.map((_, i) => {
        const slot = comparisonMonths[i];
        return slot ? monthValue(m.key, previous, slot, leadTime.previous?.[i]) : null;
      });
    }
  });
  return { months, current, previous: previous ? before : null };
};

/* ---------------- månadstabell ---------------- */

/**
 * Talen i tabellcellerna. Kortare än trendtabellens — tolv kolumner ska rymmas på ett A4 — och
 * utan enhet, som står en gång i radens etikett i stället.
 */
const CELL_FORMAT: Record<string, (v: number) => string> = {
  delivered: (v) => nf(v),
  leadTime: (v) => nf(v, 1),
  truckFill: (v) => nf(v, 1),
  satisfaction: (v) => nf(v, 2),
  cancellations: (v) => nf(v),
  truckShare: (v) => nf(v, 1),
  utilization: (v) => nf(v, 1),
};

const UNIT: Record<string, string> = {
  leadTime: 'h',
  truckFill: '%',
  truckShare: '%',
  utilization: '%',
  satisfaction: 'av 5',
};

/**
 * Alla mått, månad för månad, med året längst till höger.
 *
 * Varje månad tonas mot samma månad i fjol och inte mot månaden innan. Säsongen är större än
 * trenden: juli ser alltid ut som ett ras mot juni, och det säger ingenting om hur det går.
 */
export const MonthlyPanel = ({
  series,
  data,
  comparisonLabel,
}: {
  series: MonthlySeries;
  data: ReportData;
  comparisonLabel: string;
}) => {
  const last = series.months[series.months.length - 1];
  return (
    <Panel $span={12}>
      <PanelHead>
        <span className="tag">MÅNAD</span>
        <h2>Månad för månad</h2>
        <span className="spacer" />
        <span className="unit">
          {series.previous
            ? 'varje månad mot samma månad i fjol'
            : 'jämförelsen kunde inte hämtas'}
        </span>
      </PanelHead>
      <TableWrap>
        <MonthTable>
          <thead>
            <tr>
              <th>Mått</th>
              {series.months.map((slot) => (
                <th key={slot.label} className="n">
                  {slot.label}
                  {slot.partial ? '*' : ''}
                </th>
              ))}
              <th className="n year">Året</th>
            </tr>
          </thead>
          <tbody>
            {MEASURES.map((m) => {
              const format = CELL_FORMAT[m.key] ?? m.format;
              const total = m.value(data);
              return (
                <tr key={m.key}>
                  <td className="name">
                    {m.label}
                    {UNIT[m.key] ? <small>{UNIT[m.key]}</small> : null}
                  </td>
                  {series.months.map((slot, i) => {
                    const now = series.current[m.key][i];
                    const before = series.previous ? series.previous[m.key][i] : null;
                    const trend = trendOf(m, now, before);
                    return (
                      <MonthCell
                        key={slot.label}
                        className="n"
                        $tone={trend?.tone ?? 'flat'}
                        title={
                          before === null
                            ? undefined
                            : `${slot.label} i fjol: ${format(before)}${trend ? ` · ${trend.text}` : ''}`
                        }
                      >
                        {now === null ? '–' : format(now)}
                      </MonthCell>
                    );
                  })}
                  <td className="n year">{total === null ? '–' : format(total)}</td>
                </tr>
              );
            })}
          </tbody>
        </MonthTable>
      </TableWrap>
      <Note>
        Grön cell är bättre än samma månad i fjol, röd sämre; ofärgad är oförändrad eller saknar
        jämförelse. Håll muspekaren över en cell för fjolårets tal.
        {last?.partial ? (
          <>
            {' '}
            * {last.label} är inte slut; den jämförs mot {last.label} i fjol fram till samma
            datum.
          </>
        ) : null}{' '}
        Året är hela periodens tal, samma som i trendtabellen mot {comparisonLabel}.
      </Note>
    </Panel>
  );
};

/* ---------------- mått per månad som staplar ---------------- */

const CHART_H = 140;

/**
 * Var staplarna börjar och slutar, per mått.
 *
 * Staplarna börjar på skalans botten, inte på det lägsta värdet: en stapel läses som en mängd,
 * och en axel som börjar på 4,8 får en hundradels skillnad i kundnöjdhet att se ut som en
 * halvering. Andelar ritas mot 100 % — eller högre, när utnyttjandet går över taket — och
 * betyget mot skalan 1–5. Små rörelser syns därför knappt i staplarna; talen står i tabellen.
 */
const SCALE: Record<string, (values: number[]) => [number, number]> = {
  truckFill: (v) => [0, Math.max(100, ...v)],
  truckShare: (v) => [0, Math.max(100, ...v)],
  utilization: (v) => [0, Math.max(100, ...v)],
  satisfaction: () => [1, 5],
};

const defaultScale = (values: number[]): [number, number] => [0, Math.max(1, ...values)];

/** Ett mått per månad som stapelpar, årets bredvid fjolårets. */
export const MonthlyChartPanel = ({
  series,
  measureKey,
  span = 12,
}: {
  series: MonthlySeries;
  measureKey: string;
  span?: number;
}) => {
  const measure = MEASURES.find((m) => m.key === measureKey);
  if (!measure) return null;

  const now = series.current[measureKey] ?? [];
  const before = series.previous?.[measureKey] ?? [];
  const present = [...now, ...before].filter((v): v is number => v !== null);
  const [floor, ceiling] = (SCALE[measureKey] ?? defaultScale)(present);
  const height = (v: number | null | undefined) =>
    v === null || v === undefined
      ? 0
      : Math.round((Math.max(0, v - floor) / (ceiling - floor || 1)) * CHART_H);
  const format = CELL_FORMAT[measureKey] ?? measure.format;
  const last = series.months[series.months.length - 1];

  return (
    <Panel $span={span}>
      <PanelHead>
        <h2>{measure.label} per månad</h2>
        <span className="spacer" />
        <span className="unit">{UNIT[measureKey] ?? 'antal'}</span>
      </PanelHead>
      <MonthBars style={{ height: CHART_H + 40 }}>
        {series.months.map((slot, i) => (
          <div key={slot.label} className="month">
            <div className="value">{now[i] === null ? '–' : format(now[i] as number)}</div>
            <div className="pair" style={{ height: CHART_H }}>
              {series.previous ? (
                <div
                  className="before"
                  style={{ height: height(before[i]) }}
                  title={`${slot.label} i fjol: ${before[i] === null ? '–' : format(before[i] as number)}`}
                />
              ) : null}
              <div
                className={slot.partial ? 'now partial' : 'now'}
                style={{ height: height(now[i]) }}
                title={`${slot.label}: ${now[i] === null ? '–' : format(now[i] as number)}`}
              />
            </div>
            <div className="label">
              {slot.label}
              {slot.partial ? '*' : ''}
            </div>
          </div>
        ))}
      </MonthBars>
      <Note>
        Blå stapel är i år, grå samma månad i fjol
        {measure.goodWhen === 'down' ? ' — här är lägre bättre' : ''}.
        {last?.partial ? ` * ${last.label} är månaden hittills.` : ''}
      </Note>
    </Panel>
  );
};

/* ---------------- staplar i rubrikbrickorna ---------------- */

/**
 * Månaderna som små staplar under en rubrikbricka.
 *
 * Skalan börjar inte på noll. Brickan ska visa rörelsen mellan månaderna, och en ledtid som
 * pendlar mellan 64 och 82 timmar blir tolv lika höga staplar på en nollbaserad skala.
 * Tabellen längre ner har talen för den som vill läsa nivån.
 */
export const HeadlineSpark = ({
  measureKey,
  series,
}: {
  measureKey: string;
  series: MonthlySeries | null;
}) => {
  if (!series) return null;
  const values = series.current[measureKey] ?? [];
  const present = values.filter((v): v is number => v !== null);
  if (present.length < 2) return null;

  const max = Math.max(...present);
  const min = Math.min(...present);
  const span = max - min || 1;
  const height = (v: number | null) => (v === null ? 0 : 4 + ((v - min) / span) * 20);

  return (
    <Spark viewBox={`0 0 ${values.length * 10} 24`} preserveAspectRatio="none" aria-hidden>
      {values.map((v, i) => (
        <rect
          key={series.months[i].label}
          x={i * 10 + 1}
          y={24 - height(v)}
          width={7}
          height={height(v)}
          fill="var(--s1)"
          opacity={series.months[i].partial ? 0.45 : 1}
        />
      ))}
    </Spark>
  );
};
